import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { Page } from '../../components/page';
import {
  Feature,
  getFeatureByAddOn,
  getProductId,
  PlanInterval,
  SubscriptionSource,
  trueSelfServeAddOn,
} from '../../models';
import { useQuery } from '../../routes';
import { fromOrder, fromSubscription, useAppDispatch } from '../../store';
import { TrueSeatSelect } from './component/true-platform/TrueSeatSelect';
import { TrueBundlePlanSelect } from './component/true-platform/TrueBundlePlanSelect';
import { PriceSummary } from './component/true-platform/PriceSummary';
import { OrderSummary } from './component/true-platform/OrderSummary';
import { TestCloudSession } from './component/true-platform/TestCloudSession';
import { LoadingProgress } from '../../layout';
import { Panel } from '../../components/common/Panel';
import { useGetTrackingCookies, usePlanCartSnapshot, usePreviewTrue } from '../../hook';
import {
  getCycleFromPathParam,
  getPlanProductCycleFromPathParam,
  getQuotaFromPathParam,
  sendTrackingData,
  TrackingPayload,
} from '../../utils/utils';
import OrderDetailSection from './component/OrderDetailSection';
import { PlanStepNav } from './component/PlanStepNav';
import { BillingToggle } from './component/true-platform/BillingToggle';
import { PromoBanner } from './component/promo-banner/PromoBanner';
import { DEFAULT_TRUE_BUNDLE_FEATURE } from './component/true-platform/trueBundlePlans';
import { useLaunchDarkly } from '../../launchdarkly';

const TRUE_SUBSCRIPTION_FEATURES = [
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
];

export const PlanV2 = () => {
  const { get } = useQuery();
  const { flags } = useLaunchDarkly();
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const trueBundleEnabled = flags?.feTruePlatformAndTrueAutomationEnabled === true;
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const trueSubscriptions = useMemo(
    () => accountSubscriptions.filter(
      sub => sub.source === SubscriptionSource.RECURLY
        && TRUE_SUBSCRIPTION_FEATURES.includes(sub.feature),
    ),
    [accountSubscriptions],
  );
  const legacyTrueSubscription = trueSubscriptions.find(
    sub => sub.feature === Feature.TRUE_TEAM
      || sub.feature === Feature.TRUE_ENTERPRISE,
  );
  const subscription = legacyTrueSubscription ?? trueSubscriptions[0];
  const trueTeamQuota = getQuotaFromPathParam(Feature.TRUE_TEAM);
  const trueTeamCycle = getCycleFromPathParam(Feature.TRUE_TEAM);
  const truePlatformOnlyCycle = getCycleFromPathParam(Feature.TRUE_PLATFORM_ONLY);
  const defaultTrueBundleCycle = getCycleFromPathParam(DEFAULT_TRUE_BUNDLE_FEATURE);
  const selectedProductCycle = getPlanProductCycleFromPathParam();
  const legacyTrueAddOnQuotas = trueSelfServeAddOn.map(
    addOn => getQuotaFromPathParam(getFeatureByAddOn(addOn)) ?? 0,
  );
  const legacyTrueAddOnsKey = legacyTrueAddOnQuotas.join('|');
  const legacyTrueAddOns = useMemo(
    () => trueSelfServeAddOn.reduce<Record<string, number>>((addOns, addOn, index) => {
      const quota = legacyTrueAddOnQuotas[index];
      if (quota > 0) {
        addOns[addOn.toUpperCase()] = quota;
      }
      return addOns;
    }, {}),
    [legacyTrueAddOnsKey],
  );
  const hasLegacyTruePlatformQuery = trueTeamQuota !== undefined
    || trueTeamCycle !== undefined;
  const useLegacyTruePlatform = !trueBundleEnabled
    && (legacyTrueSubscription !== undefined || hasLegacyTruePlatformQuery);
  const cycle = useLegacyTruePlatform
    ? trueTeamCycle
    : truePlatformOnlyCycle ?? defaultTrueBundleCycle ?? selectedProductCycle;
  const [billingInterval, setBillingInterval] = useState<PlanInterval>(
    subscription?.billingInterval ?? PlanInterval.YEAR,
  );
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const hasPendingOrder = !!pendingOrder && pendingOrder.length > 0;
  const isAnnual = billingInterval === PlanInterval.YEAR;
  const disableBillingToggle = hasPendingOrder
    || (subscription !== undefined && subscription.billingInterval !== PlanInterval.MONTH);
  const disabledActionSx = hasPendingOrder
    ? { pointerEvents: 'none' as const, opacity: 0.5 }
    : undefined;
  const billingToggle = (
    <BillingToggle
      key="billing-toggle"
      isAnnual={isAnnual}
      setBillingInterval={setBillingInterval}
      toggelDisabled={disableBillingToggle}
      showSaveBadge={useLegacyTruePlatform}
      onChangeBillingInterval={interval => trackPlanPageAction({
        field: 'billing_interval',
        object: useLegacyTruePlatform ? Feature.TRUE_TEAM : 'true_bundle',
        action: 'change_interval',
        billing_interval: PlanInterval.getPlanIntervalCode(interval),
      })}
    />
  );

  useEffect(() => {
    dispatch(fromOrder.setAddOns(useLegacyTruePlatform ? legacyTrueAddOns : {}));
  }, [dispatch, useLegacyTruePlatform, legacyTrueAddOns]);

  const loading = useSelector(fromOrder.selectLoading);

  useEffect(() => {
    if (cycle && (!subscription || subscription.billingInterval === PlanInterval.MONTH)) {
      setBillingInterval(cycle);
    }
  }, [cycle]);

  const fetchData = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromOrder.resetOrderPreviewRequest()),
        dispatch(fromOrder.resetOrderPreviewResponse()),
      ]);
    }
  };

  usePreviewTrue();

  const trackPlanPageAction = (payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: useLegacyTruePlatform ? 'true_team_legacy' : 'true_bundle',
        ...payload,
        ...trackingCookies,
        cart_snapshot: cartSnapshot,
      },
    );
  };

  useEffect(() => {
    dispatch(fromOrder.doFetchAllOrder(accountId));
  }, [accountId, dispatch]);

  return (
    <Page
      id="plan-v2-page"
      key="plan-v2-page"
      headerId="page.plan.v2.header"
      fetchData={fetchData}
      topBanner={useLegacyTruePlatform ? <PromoBanner accountId={accountId} /> : undefined}
      background={useLegacyTruePlatform ? undefined : '#F7F9F8'}
      hideHeader={!useLegacyTruePlatform}
    >
      {!useLegacyTruePlatform && (
        <>
          <PlanStepNav currentStep={1} />
          {/* Temporarily hidden — restore by re-adding <LimitedOfferBanner /> */}
        </>
      )}
      {pendingOrder && pendingOrder.length > 0
        && (
          <Box
            id="expand-plan-banner"
            display="flex"
            mt={2}
            border="1px solid #dfbc4a"
            borderRadius={2}
            maxWidth={1825}
          >
            <OrderDetailSection pendingOrder={pendingOrder[0]} />
          </Box>
        )}
      {!useLegacyTruePlatform && (
        <Box id="plan-v2-heading" pt={4}>
          <Typography
            sx={{
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 30,
              fontWeight: 500,
              letterSpacing: '-.02em',
              color: '#16141d',
            }}
          >
            <FormattedMessage id="page.plan.v2.true.bundle.add.seats.title" />
          </Typography>
          <Typography sx={{ mt: 1.25, fontSize: 13, lineHeight: 1.5, color: '#73718a' }}>
            <Box component="span" sx={{ color: '#16141d', fontWeight: 600 }}>
              <FormattedMessage id="page.plan.v2.true.bundle.included.title" />
              {': '}
            </Box>
            <FormattedMessage id="page.plan.v2.true.bundle.included.description" />
          </Typography>
        </Box>
      )}
      <Box
        id="plan-v2-content"
        key="plan-v2-content"
        display="flex"
        py={3}
        sx={theme => ({
          flexDirection: 'row',
          [theme.breakpoints.down('tabletL')]: {
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        })}
      >
        {/* Left panel */}
        <Panel
          id="plan-v2"
          step={useLegacyTruePlatform ? 1 : undefined}
          headerLabel={useLegacyTruePlatform
            ? <FormattedMessage id="page.plan.v2.header" />
            : undefined}
          headerRightItem={useLegacyTruePlatform ? billingToggle : undefined}
          transparent={!useLegacyTruePlatform}
          footer={useLegacyTruePlatform ? (
            <Box sx={disabledActionSx}>
              <TestCloudSession
                key="test-cloud-session"
                billingInterval={billingInterval}
              />
            </Box>
          ) : undefined}
          sx={{
            width: '100%',
            minWidth: '70%',
            maxWidth: 1200,
            pr: 4,
            pb: 4,
            borderRight: useLegacyTruePlatform ? '1px solid #BBBBBB' : 'none',
          }}
        >
          <Box px={useLegacyTruePlatform ? 1 : 0}>
            {useLegacyTruePlatform && (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                mb={2}
                sx={theme => ({
                  gap: theme.spacing(2),
                  [theme.breakpoints.down('tablet')]: {
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                  },
                })}
              >
                <Typography fontWeight={600} lineHeight="135%" fontSize={32} color="#4C5BAA">
                  <FormattedMessage id="page.plan.v2.plan.title" />
                </Typography>
              </Box>
            )}

            <Box sx={disabledActionSx}>
              {/* Number of seats */}
              {useLegacyTruePlatform
                ? (
                  <TrueSeatSelect
                    productId={getProductId(Feature.TRUE_TEAM)}
                    billingInterval={billingInterval}
                  />
                )
                : (
                  <TrueBundlePlanSelect />
                )}

              {/* Pricing row */}
              {useLegacyTruePlatform && (
                <PriceSummary isAnnual={isAnnual} />
              )}
            </Box>
          </Box>
        </Panel>

        {/* Right panel — Order Summary */}
        <Box
          id="plan-v2-right"
          sx={theme => ({
            width: '30%',
            flexShrink: 0,
            [theme.breakpoints.down('tabletL')]: {
              width: '100%',
            },
          })}
        >
          <OrderSummary
            isAnnual={isAnnual}
            groupByInterval={!useLegacyTruePlatform}
            showCoupon={!useLegacyTruePlatform}
          />
        </Box>
      </Box>
      {loading && (<LoadingProgress fullscreen />)}
    </Page>
  );
};
